import { ADMITTED_CATEGORIES, appointmentBandOf, categoryKey, isHeld } from '@volee/api-client'
import type {
  CategoryColumns,
  AgeGroup,
  AppointmentRole,
  BenchJobState,
  BenchStepStatus,
  ClubStatus,
  KitCondition,
  LeagueTier,
  InterventionState,
  AskedMaintenanceAction,
  MaintenanceStatus,
  PartOutcome,
  MatchAnalysisState,
  MatchLeg,
  MatchRead,
  MatchSide,
  MatchStatus,
  Me,
  PlayerFoot,
  PlayerPosition,
  RecordingStatus,
  RegistrationRequestStatus,
  Sex,
  StatefulAppointment,
  TeamGender,
  TeamRead,
  UserSubscription,
  WritableMatchStatus,
} from '@volee/api-client'
import type { StatusTextTone, StatusVariant, StepMeterState } from '@volee/ui'

import type { StaffRole, TeamSheetState } from './en/domain.ts'
import {
  EN_AGE_GROUPS,
  EN_APPOINTMENT_ROLES,
  EN_BENCH_JOB_STATES,
  EN_BENCH_STEPS,
  EN_BENCH_STEP_STATUSES,
  EN_CLUB_STATUSES,
  EN_GUARDIAN_RELATIONSHIPS,
  EN_KIT_CONDITIONS,
  EN_KIT_REASONS,
  EN_LEAGUE_TIERS,
  EN_MAINTENANCE_ACTIONS,
  EN_INTERVENTION_PARTS,
  EN_INTERVENTION_STATES,
  EN_MAINTENANCE_STATUSES,
  EN_PART_OUTCOMES,
  EN_MATCH_LEGS,
  EN_MATCH_STATUSES,
  EN_PLAYER_FEET,
  EN_MATCH_ANALYSIS_STATES,
  EN_TEAM_SHEET_STATES,
  EN_RECORDING_STATUSES,
  EN_SEGMENT_QUALITIES,
  EN_LEAGUE_TIER_ADJECTIVES,
  EN_PLAYER_POSITIONS,
  EN_REGISTRATION_REQUEST_STATUSES,
  EN_SEXES,
  EN_STAFF_ROLES,
  EN_SUBSCRIPTION_STATUSES,
  EN_TEAM_GENDERS,
  EN_UNKNOWN_VALUE,
} from './en/domain.ts'
import {
  IT_AGE_GROUPS,
  IT_APPOINTMENT_ROLES,
  IT_BENCH_JOB_STATES,
  IT_BENCH_STEPS,
  IT_BENCH_STEP_STATUSES,
  IT_CLUB_STATUSES,
  IT_GUARDIAN_RELATIONSHIPS,
  IT_KIT_CONDITIONS,
  IT_KIT_REASONS,
  IT_LEAGUE_TIERS,
  IT_MAINTENANCE_ACTIONS,
  IT_INTERVENTION_PARTS,
  IT_INTERVENTION_STATES,
  IT_MAINTENANCE_STATUSES,
  IT_PART_OUTCOMES,
  IT_MATCH_LEGS,
  IT_MATCH_STATUSES,
  IT_PLAYER_FEET,
  IT_MATCH_ANALYSIS_STATES,
  IT_TEAM_SHEET_STATES,
  IT_RECORDING_STATUSES,
  IT_SEGMENT_QUALITIES,
  IT_LEAGUE_TIER_ADJECTIVES,
  IT_PLAYER_POSITIONS,
  IT_REGISTRATION_REQUEST_STATUSES,
  IT_SEXES,
  IT_STAFF_ROLES,
  IT_SUBSCRIPTION_STATUSES,
  IT_TEAM_GENDERS,
  IT_UNKNOWN_VALUE,
} from './it/domain.ts'
import { locale, type Locale } from './locale.ts'
import { formatDate, formatKickOff, todayIsoDate } from './format.ts'
import { interpolate } from './interpolate.ts'
import { messages } from './ui.ts'

// Domain enums in the active language. No enum is ever shown raw (guardrail §5):
// a value outside the catalogue gives UNKNOWN_VALUE, not itself.
export type {
  AgeGroup,
  AppointmentRole,
  GuardianRelationship,
  LeagueTier,
  PlayerFoot,
  PlayerPosition,
  Sex,
  StaffRole,
  TeamGender,
  TeamSheetState,
} from './en/domain.ts'

const EN = locale === 'en'

export const APPOINTMENT_ROLES = EN ? EN_APPOINTMENT_ROLES : IT_APPOINTMENT_ROLES
export const PLAYER_POSITIONS = EN ? EN_PLAYER_POSITIONS : IT_PLAYER_POSITIONS
export const PLAYER_FEET = EN ? EN_PLAYER_FEET : IT_PLAYER_FEET
export const AGE_GROUPS = EN ? EN_AGE_GROUPS : IT_AGE_GROUPS
export const CLUB_STATUSES = EN ? EN_CLUB_STATUSES : IT_CLUB_STATUSES
export const GUARDIAN_RELATIONSHIPS = EN ? EN_GUARDIAN_RELATIONSHIPS : IT_GUARDIAN_RELATIONSHIPS
export const STAFF_ROLES = EN ? EN_STAFF_ROLES : IT_STAFF_ROLES
export const REGISTRATION_REQUEST_STATUSES = EN
  ? EN_REGISTRATION_REQUEST_STATUSES
  : IT_REGISTRATION_REQUEST_STATUSES
export const TEAM_GENDERS = EN ? EN_TEAM_GENDERS : IT_TEAM_GENDERS
export const SEXES = EN ? EN_SEXES : IT_SEXES
export const LEAGUE_TIERS = EN ? EN_LEAGUE_TIERS : IT_LEAGUE_TIERS
export const LEAGUE_TIER_ADJECTIVES = EN ? EN_LEAGUE_TIER_ADJECTIVES : IT_LEAGUE_TIER_ADJECTIVES
export const MATCH_STATUSES = EN ? EN_MATCH_STATUSES : IT_MATCH_STATUSES
export const RECORDING_STATUSES = EN ? EN_RECORDING_STATUSES : IT_RECORDING_STATUSES
export const MATCH_ANALYSIS_STATES = EN ? EN_MATCH_ANALYSIS_STATES : IT_MATCH_ANALYSIS_STATES
export const TEAM_SHEET_STATE_LABELS = EN ? EN_TEAM_SHEET_STATES : IT_TEAM_SHEET_STATES
export const SEGMENT_QUALITIES = EN ? EN_SEGMENT_QUALITIES : IT_SEGMENT_QUALITIES
export const MATCH_LEGS = EN ? EN_MATCH_LEGS : IT_MATCH_LEGS
export const BENCH_JOB_STATES = EN ? EN_BENCH_JOB_STATES : IT_BENCH_JOB_STATES
export const BENCH_STEPS = EN ? EN_BENCH_STEPS : IT_BENCH_STEPS
export const BENCH_STEP_STATUSES = EN ? EN_BENCH_STEP_STATUSES : IT_BENCH_STEP_STATUSES
export const MAINTENANCE_ACTIONS = EN ? EN_MAINTENANCE_ACTIONS : IT_MAINTENANCE_ACTIONS
export const MAINTENANCE_STATUSES = EN ? EN_MAINTENANCE_STATUSES : IT_MAINTENANCE_STATUSES
export const INTERVENTION_STATES = EN ? EN_INTERVENTION_STATES : IT_INTERVENTION_STATES
export const INTERVENTION_PARTS = EN ? EN_INTERVENTION_PARTS : IT_INTERVENTION_PARTS
export const PART_OUTCOMES = EN ? EN_PART_OUTCOMES : IT_PART_OUTCOMES
export const KIT_CONDITIONS = EN ? EN_KIT_CONDITIONS : IT_KIT_CONDITIONS
export const KIT_REASONS = EN ? EN_KIT_REASONS : IT_KIT_REASONS
export const SUBSCRIPTION_STATUSES = EN ? EN_SUBSCRIPTION_STATUSES : IT_SUBSCRIPTION_STATUSES

/** Shown in place of a value no catalogue knows. */
export const UNKNOWN_VALUE = EN ? EN_UNKNOWN_VALUE : IT_UNKNOWN_VALUE

/** The values of a rank object, in rank order. One unchecked cast instead of four:
 * `Object.keys` returns `string[]`, so each site was asserting the element type itself. */
function orderOf<TValue extends string>(rank: Record<TValue, number>): readonly TValue[] {
  return Object.keys(rank) as TValue[]
}

/** Which `Badge` variant draws a status. Here and not in `@volee/ui` because which state
 * counts as "good" is a domain decision — and a colour is not a translation. */
export const CLUB_STATUS_VARIANTS: Record<ClubStatus, StatusVariant> = {
  lead: 'neutral',
  in_review: 'info',
  active: 'success',
  suspended: 'warning',
  archived: 'danger',
}

/** The statuses in the order a club moves through them. `satisfies` is the point: one added
 * to the contract fails here rather than quietly missing from a filter or a tile. */
const CLUB_STATUS_RANK = {
  lead: 0,
  in_review: 1,
  active: 2,
  suspended: 3,
  archived: 4,
} satisfies Record<ClubStatus, number>

export const CLUB_STATUS_ORDER = orderOf(CLUB_STATUS_RANK)

/** Which `Badge` variant draws a recording. Only `complete` is an outcome: `scheduled`
 * and `in_progress` are both normal for hours, and `incomplete` is the one that says a
 * match came back with holes in it. */
export const RECORDING_STATUS_VARIANTS: Record<RecordingStatus, StatusVariant> = {
  scheduled: 'neutral',
  in_progress: 'info',
  complete: 'success',
  incomplete: 'warning',
  failed: 'danger',
  processed: 'success',
}

/** Grey words for every state but the one that stopped: that is the red the eye looks for. */
export const BENCH_JOB_STATE_TONES: Record<BenchJobState, StatusTextTone> = {
  offered: 'running',
  accepted: 'running',
  saved: 'running',
  done: 'done',
  failed: 'failed',
  taken_elsewhere: 'neutral',
}

/** A step not reported yet is `todo`: the caller maps `null` itself. */
export const BENCH_STEP_STATUS_TONES: Record<BenchStepStatus, StepMeterState> = {
  running: 'running',
  ok: 'done',
  failed: 'failed',
}

/** The specs' order (2026-10-21 § 7, 2026-10-22 § 5): the router's, the camera's, the reading. */
const MAINTENANCE_ACTION_RANK = {
  router_wifi_off: 0,
  camera_dhcp_reservation: 1,
  camera_rtsp_on: 2,
  camera_reboot: 3,
  camera_apply_profile: 4,
  read_inventory: 5,
} satisfies Record<AskedMaintenanceAction, number>

export const MAINTENANCE_ACTION_ORDER = orderOf(MAINTENANCE_ACTION_RANK)

export const MAINTENANCE_STATUS_VARIANTS: Record<MaintenanceStatus, StatusVariant> = {
  requested: 'neutral',
  running: 'info',
  ok: 'success',
  failed: 'danger',
  // The box said no for a reason (a recording open): an act to repeat later, not a fault.
  declined: 'warning',
  expired: 'neutral',
}

export const INTERVENTION_STATE_VARIANTS: Record<InterventionState, StatusVariant> = {
  open: 'warning',
  in_progress: 'info',
  closed: 'success',
  cancelled: 'neutral',
}

/** The order a person reads them in a select: the most common fate first. */
const PART_OUTCOME_RANK = {
  reusable: 0,
  broken: 1,
  disposed: 2,
} satisfies Record<PartOutcome, number>

export const PART_OUTCOME_ORDER = orderOf(PART_OUTCOME_RANK)

/** Silence is the one that alarms: a kit never seen may simply not be switched on yet. */
export const KIT_CONDITION_VARIANTS: Record<KitCondition, StatusVariant> = {
  never_seen: 'warning',
  silent: 'danger',
  check: 'warning',
  ok: 'success',
}

/** Which `Badge` variant draws a plan. Only an overdue payment asks for action; the rest
 * are either running or over. */
export const SUBSCRIPTION_STATUS_VARIANTS: Record<UserSubscription['status'], StatusVariant> = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  paused: 'neutral',
  incomplete: 'neutral',
  incomplete_expired: 'neutral',
  canceled: 'neutral',
  unpaid: 'neutral',
}

/** A draft is work in progress, not a fault: only an absent convocazione is worth the warning. */
export const TEAM_SHEET_STATE_VARIANTS: Record<TeamSheetState, StatusVariant> = {
  none: 'warning',
  draft: 'neutral',
  published: 'success',
}

/** What UbiTrack closed without a result: sent again on the same match, «Invia di nuovo». */
export const RESENDABLE_ANALYSIS_STATES: readonly MatchAnalysisState[] = ['failed', 'rejected']

/** Being tagged or tagged at UbiTrack: the server refuses to send these again. */
export const SETTLED_ANALYSIS_STATES: readonly MatchAnalysisState[] = [
  'accepted',
  'completed',
  'imported',
]

/** Which `Badge` variant draws an analysis. A send is waiting on UbiTrack, not on us. */
export const MATCH_ANALYSIS_STATE_VARIANTS: Record<MatchAnalysisState, StatusVariant> = {
  not_submitted: 'neutral',
  submitted: 'info',
  accepted: 'info',
  rejected: 'warning',
  completed: 'success',
  failed: 'danger',
  imported: 'success',
}

/** Which `Badge` variant draws a request's state. Neither queue state is «good»: one is
 * waiting on us, the other on the applicant, and only `approved` is an outcome. */
export const REGISTRATION_REQUEST_STATUS_VARIANTS: Record<
  RegistrationRequestStatus,
  StatusVariant
> = {
  submitted: 'info',
  needs_integration: 'warning',
  approved: 'success',
  rejected: 'danger',
}

/** The variant for a status the contract types as a bare `string`, so an unknown fifth state
 * gets a neutral pill instead of an undefined one — the pair of `translateDomainValue`. */
export function registrationRequestStatusVariant(status: string): StatusVariant {
  return Object.hasOwn(REGISTRATION_REQUEST_STATUS_VARIANTS, status)
    ? REGISTRATION_REQUEST_STATUS_VARIANTS[status as RegistrationRequestStatus]
    : 'neutral'
}

/** The queue's order: what needs doing first, then what is settled. `satisfies`, for the
 * reason `CLUB_STATUS_RANK` carries. */
const REGISTRATION_REQUEST_STATUS_RANK = {
  submitted: 0,
  needs_integration: 1,
  approved: 2,
  rejected: 3,
} satisfies Record<RegistrationRequestStatus, number>

export const REGISTRATION_REQUEST_STATUS_ORDER = orderOf(REGISTRATION_REQUEST_STATUS_RANK)

/** The FIGC categories oldest first — the order `services/api` sorts a club's teams in, so
 * a select and the list it feeds agree. `satisfies`, for the reason above. */
const AGE_GROUP_RANK = {
  seniores: 0,
  juniores: 1,
  allievi: 2,
  giovanissimi: 3,
  esordienti: 4,
  pulcini: 5,
} satisfies Record<AgeGroup, number>

export const AGE_GROUP_ORDER = orderOf(AGE_GROUP_RANK)

// The order of a team sheet, not the alphabet.
const PLAYER_POSITION_RANK = {
  goalkeeper: 0,
  defender: 1,
  midfielder: 2,
  forward: 3,
} satisfies Record<PlayerPosition, number>

export const PLAYER_POSITION_ORDER = orderOf(PLAYER_POSITION_RANK)

/** The picker's order: right first, because it is the normal case. */
export const FOOT_ORDER: readonly PlayerFoot[] = ['right', 'left', 'both']

// Alphabetical in both languages, which is the only order two values can claim.
const SEX_RANK = { female: 0, male: 1 } satisfies Record<Sex, number>

export const SEX_ORDER = orderOf(SEX_RANK)

/** The organigramma in the order it is read. It mirrors `APPOINTMENT_ROLE_SCOPES` in
 * `services/api/db/enums.py`, and what `satisfies` cannot catch:
 * apps/admin-panel/docs/appointments-perche-non-basta-organigramma.md. */
const APPOINTMENT_ROLE_RANK = {
  official: 0,
  team_official: 1,
  coach: 2,
} satisfies Record<AppointmentRole, number>

export const APPOINTMENT_ROLE_ORDER = orderOf(APPOINTMENT_ROLE_RANK)

const TEAM_GENDER_RANK = { male: 0, female: 1, open: 2 } satisfies Record<TeamGender, number>

export const TEAM_GENDER_ORDER = orderOf(TEAM_GENDER_RANK)

/** Widest first: it is the order a committee prints them in. `satisfies`, for the reason
 * `CLUB_STATUS_RANK` carries. */
const LEAGUE_TIER_RANK = {
  provincial: 0,
  interprovincial: 1,
  regional: 2,
  elite: 3,
} satisfies Record<LeagueTier, number>

export const LEAGUE_TIER_ORDER = orderOf(LEAGUE_TIER_RANK)

/** The age limits the FIGC prints, 0 being the seniores sentinel. Our own shortlist, not a
 * backend enum, and a select because nobody guesses that 0 means "no limit". */
export const UNDER_AGE_OPTIONS: readonly number[] = [0, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9]

/** The letter of a club's first side, which the federation leaves unwritten: on C.U. 44 of
 * Chiavari (2025/26) it is the bare club name, and `SQ.B` onwards name the others. */
const FIRST_TEAM_LETTER = 'A'

/** Said only when it says something: most clubs field one side per category and gender, and
 * «Squadra A» on every one of them would be noise — the bulletin omits it for the same reason. */
export function teamLetterLabel(letter: string): string | null {
  if (letter === FIRST_TEAM_LETTER) return null
  return interpolate(messages.club.teamLetter, { letter })
}

/** How a squad reads: it has no name, only a category, an age limit, a gender and the
 * federation's letter. Two files joined the halves with the same separator before this existed.
 * Neither the gender nor the letter is optional: without them one label named two squads. */
export function teamLabel(team: {
  age_group: string
  under_age: number
  gender: string
  letter: string
}): string {
  const category = translateDomainValue(AGE_GROUPS, team.age_group)
  const gender = translateDomainValue(TEAM_GENDERS, team.gender)
  const columns = `${category} · ${underAgeLabel(team.age_group, team.under_age)} · ${gender}`
  const letter = teamLetterLabel(team.letter)
  return letter === null ? columns : `${columns} · ${letter}`
}

/** How a girone reads: the category's name, not its four columns spelled out. Why, and what
 * it costs that `name` is free text: apps/admin-panel/docs/registro-dei-campionati.md. */
export function leagueGroupLabel(group: {
  code: string
  season_year: number
  category: { name: string }
}): string {
  const season = seasonLabel(group.season_year)
  const code = interpolate(messages.league.groupCode, { code: group.code })
  return `${group.category.name} — ${code} · ${season}`
}

/** A girone a delegation's comunicato created: «Delegazione Chiavari · Girone C». The slug
 *  is spelled out, never looked up — a table of delegation names here would be a second
 *  list, free to disagree with the server's. */
export function delegationGironeLabel(delegation: string, code: string): string {
  return interpolate(messages.league.lndDelegationKey, {
    delegation: spelledDelegation(delegation),
    code,
  })
}

/** «Delegazione Chiavari», from the slug, spelled as `delegationGironeLabel` spells it. */
export function delegationName(delegation: string): string {
  return interpolate(messages.league.delegationName, { delegation: spelledDelegation(delegation) })
}

function spelledDelegation(slug: string): string {
  const spelled = slug.replace(/-/g, ' ')
  return spelled.charAt(0).toUpperCase() + spelled.slice(1)
}

/** The words for an incarico with no squad — the only ambiguous case, since a team in hand is
 * always its own band. `appointmentBandOf` owns that ambiguity, so nothing here restates it. */
export function bandlessLabel(role: AppointmentRole): string {
  const band = appointmentBandOf(role, null)
  return band === 'unassigned' ? messages.official.noTeamBand : messages.official.clubBand
}

/** Which squad an incarico is for, or which of the two ways of having none. Three tables read
 * it, and they must not answer the same row differently. */
export function scopeLabel(role: AppointmentRole, team: TeamRead | null | undefined): string {
  if (team != null) return teamLabel(team)
  return bandlessLabel(role)
}

/** How a person reads in a list: surname first, the order every table in the app sorts and
 * scans by. Structural, so `AppointmentRead`, `PersonRead` and a bare pair all fit. Two toasts
 * still say «Nome Cognome» — they address the operator, not a column, and are left alone. */
export function personName(person: { last_name: string; first_name: string }): string {
  return `${person.last_name} ${person.first_name}`
}

/** An account's name in the same order; null when it has none, since an account can exist
 * before its person. Each caller decides what stands in for it. */
export function accountName(account: {
  last_name: string | null
  first_name: string | null
}): string | null {
  const name = [account.last_name, account.first_name].filter(Boolean).join(' ')
  return name === '' ? null : name
}

/** How an age limit reads. 0 is the sentinel, and it does not read as "Under 0". */
export function underAgeLabel(ageGroup: string, years: number): string {
  if (ageGroup === 'seniores') return messages.club.seniores
  // 0 off the seniores is a squad of both years, «Pulcini 1° e 2° anno».
  if (years === 0) return messages.club.bothYears
  return interpolate(messages.club.underAgeOption, { years })
}

/** «Allievi U17 · Maschile», «Pulcini 1° e 2° anno · Mista», «Seniores · Maschile»: one category, one line. */
export function categoryLabel(category: CategoryColumns): string {
  const values = {
    group: translateDomainValue(AGE_GROUPS, category.age_group),
    gender: translateDomainValue(TEAM_GENDERS, category.gender),
    years: category.under_age,
  }
  if (category.under_age !== 0) return interpolate(messages.club.categoryOption, values)
  if (category.age_group === 'seniores')
    return interpolate(messages.club.categoryOptionSeniores, values)
  return interpolate(messages.club.categoryOptionBothYears, values)
}

/** The four facts of a league category as tags: «Allievi», «Provinciale», «Maschile», «Under 17».
 *  The seniores have no limit to state, and `underAgeLabel` would only say «Seniores» again. */
export function leagueCategoryFacts(
  category: CategoryColumns & { readonly tier: LeagueTier },
): string[] {
  const facts = [
    translateDomainValue(AGE_GROUPS, category.age_group),
    translateDomainValue(LEAGUE_TIERS, category.tier),
    translateDomainValue(TEAM_GENDERS, category.gender),
  ]
  if (category.age_group === 'seniores') return facts
  return [...facts, underAgeLabel(category.age_group, category.under_age)]
}

/** The admitted categories as select options, plus the held one when a row from before the list
 *  holds another: a value outside the options would render as a blank. */
export function categoryOptions(
  held: CategoryColumns | undefined,
): readonly { value: string; label: string }[] {
  const options = ADMITTED_CATEGORIES.map((category) => ({
    value: categoryKey(category),
    label: categoryLabel(category),
  }))
  if (held === undefined || options.some((option) => option.value === categoryKey(held))) {
    return options
  }
  return [...options, { value: categoryKey(held), label: categoryLabel(held) }]
}

/** The internal roles, widest access first. From a rank and not from a label catalogue's
 * keys: key order there is a translation detail, free to diverge between locales. */
const STAFF_ROLE_RANK = {
  admin: 0,
  operations: 1,
  support: 2,
} satisfies Record<StaffRole, number>

export const STAFF_ROLE_ORDER = orderOf(STAFF_ROLE_RANK)

/** How an access window reads: label and emphasis from one bit, so two side-by-side
 * branches on `is_active` cannot get out of step. Revoked is neutral, not an incident. */
export function staffState(isActive: boolean): { label: string; variant: StatusVariant } {
  if (isActive) return { label: messages.staff.inService, variant: 'success' }
  return { label: messages.staff.revoked, variant: 'neutral' }
}

/** How a tesseramento reads. Svincolato is neutral, not an incident: a season that ended
 * is the normal end of one. Same shape as `staffState`, for the same reason. */
export function registrationState(releasedOn: string | null): {
  label: string
  variant: StatusVariant
} {
  if (releasedOn === null) return { label: messages.registration.active, variant: 'success' }
  return {
    label: interpolate(messages.registration.releasedOn, { date: formatDate(releasedOn) }),
    variant: 'neutral',
  }
}

/** The two states an incarico can be filtered by. Beside `appointmentState` on purpose: the
 * badge in a row and the filter that selected it must not word the same thing differently. */
export const APPOINTMENT_STATE_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'active', label: messages.official.inCharge },
  { value: 'ended', label: messages.official.stateEnded },
]

/** How an incarico reads. Concluso is neutral, not an incident: a mandate that ended is the normal
 * end of one — and a season that ended is the *usual* one, because nobody clicks «Concludi» when
 * expiring is how a mandate finishes. Why the season counts: services/api/docs/overview.md. */
export function appointmentState(one: StatefulAppointment): {
  label: string
  variant: StatusVariant
} {
  if (one.ended_on != null) {
    return {
      label: interpolate(messages.official.endedOn, { date: formatDate(one.ended_on) }),
      variant: 'neutral',
    }
  }
  if (!isHeld(one, todayIsoDate())) {
    return { label: messages.official.endedWithSeason, variant: 'neutral' }
  }
  return { label: messages.official.inCharge, variant: 'success' }
}

/** A player's role, or the words for not having one recorded. Three pages asked. */
export function positionLabel(position: PlayerPosition | null | undefined): string {
  if (position == null) return messages.identity.positionPlaceholder
  return translateDomainValue(PLAYER_POSITIONS, position)
}

/** A person's sex, or the words for not having one recorded — the rows that predate the field. */
export function sexLabel(sex: Sex | null | undefined): string {
  if (sex == null) return messages.identity.sexPlaceholder
  return translateDomainValue(SEXES, sex)
}

/** A player's preferred foot. */
export function footLabel(foot: PlayerFoot): string {
  return translateDomainValue(PLAYER_FEET, foot)
}

/** The label of a domain value. One outside the catalogue gives `UNKNOWN_VALUE` and never
 * the raw value: showing `official` to a user is what the guardrail forbids. */
export function translateDomainValue<TValue extends string>(
  catalogue: Record<TValue, string>,
  value: string,
): string {
  return Object.hasOwn(catalogue, value) ? catalogue[value as TValue] : UNKNOWN_VALUE
}

// Explicit for the DATE_LOCALE reason (format.ts). But region names agree across
// en/en-GB/en-US, so no test below defends this choice — a principle, not evidence.
const REGION_LOCALE: Record<Locale, string> = { en: 'en-GB', it: 'it-IT' }

/** The language names are written in, and so the language they sort in. */
export const SORT_LOCALE = REGION_LOCALE[locale]

const regionNames = new Intl.DisplayNames([REGION_LOCALE[locale]], { type: 'region' })

/** A nation's name in the interface language. Here and not in `format.ts`, which already
 * imports from this file and would cycle back. A code `DisplayNames` does not know answers
 * with the code itself, which becomes UNKNOWN_VALUE (guardrail §5). */
export function nationalityLabel(code: string): string {
  const named = regionNames.of(code)
  return named === undefined || named === code ? UNKNOWN_VALUE : named
}

/** Which role a user holds. Here because the header and the dashboard both say it. */
export function staffRoleLine(me: Me): string {
  if (me.staff_role === null) return messages.session.noRole
  return interpolate(messages.session.role, {
    role: translateDomainValue(STAFF_ROLES, me.staff_role),
  })
}

/** The legs in playing order, which is also the order the calendar bands read in. */
const MATCH_LEG_RANK = {
  first: 0,
  second: 1,
  third: 2,
  fourth: 3,
} satisfies Record<MatchLeg, number>

export const MATCH_LEG_ORDER = orderOf(MATCH_LEG_RANK)

/** The two a PATCH accepts, from the contract's own subset: a third writable state added
 * on the backend reaches the select instead of being silently dropped from it. */
const WRITABLE_MATCH_STATUS_RANK = {
  scheduled: 0,
  postponed: 1,
} satisfies Record<WritableMatchStatus, number>

export const WRITABLE_MATCH_STATUS_ORDER = orderOf(WRITABLE_MATCH_STATUS_RANK)

/** Every status, in the order a fixture moves through them: a filter offers all three. */
const MATCH_STATUS_RANK = {
  scheduled: 0,
  played: 1,
  postponed: 2,
} satisfies Record<MatchStatus, number>

export const MATCH_STATUS_ORDER = orderOf(MATCH_STATUS_RANK)

/** The result as a match row takes it, or none for a fixture not played: `0 – 0` would invent
 *  one. The spoken form is for a screen reader, which reads «2 – 1» as «2 1». */
export function matchScore(
  match: MatchRead,
): { readonly home: string; readonly away: string; readonly spoken: string } | undefined {
  if (match.home_goals === null || match.away_goals === null) return undefined
  const home = String(match.home_goals)
  const away = String(match.away_goals)
  return { home, away, spoken: interpolate(messages.match.scoreSpoken, { home, away }) }
}

/** The chip a fixture wears, or none at all. `scheduled` is the normal state of every row
 * on a calendar and `played` is already said by the score: only a rinvio is news. */
export function matchStatusBadge(
  status: MatchStatus,
): { readonly label: string; readonly variant: StatusVariant } | null {
  if (status !== 'postponed') return null
  return { label: translateDomainValue(MATCH_STATUSES, status), variant: 'warning' }
}

/** A side wherever one is named: the club, and its squad where the club fields more than one
 * in a category. Both LAVAGNESE sides stand in Pulcini 2° anno girone A of C.U. 44 di Chiavari
 * and they play each other, which the club's name alone writes as «LAVAGNESE – LAVAGNESE». */
export function sideName(side: { club_short_name: string; letter: string }): string {
  const squad = teamLetterLabel(side.letter)
  return squad === null ? side.club_short_name : `${side.club_short_name} · ${squad}`
}

/** A side as `@volee/ui`'s match rows take it: the squad apart from the name, because the crest
 * draws the club's initials from the name alone. */
export function matchRowSide(side: MatchSide): {
  name: string
  squad?: string
  crestUrl?: string
} {
  return {
    name: side.club_short_name,
    squad: teamLetterLabel(side.letter) ?? undefined,
    crestUrl: side.logo_display_url ?? undefined,
  }
}

/** «Borgo Nuovo – Valle Stura». Shared because the calendar row and the fixture's own card
 * both say it, and one drifting spelling is how the same match reads as two — the reason
 * `gateway-values.tsx` exists. */
export function matchSides(match: MatchRead): string {
  return interpolate(messages.match.versus, {
    home: sideName(match.home),
    away: sideName(match.away),
  })
}

/** When and on which ground. Pinned to Italian time by `formatKickOff`, not the reader's. */
export function matchWhenAndWhere(match: MatchRead): string {
  return interpolate(messages.match.whenAndWhere, {
    when: formatKickOff(match.kicks_off_at),
    pitch: match.pitch.name,
  })
}

/** The season as the bulletin writes it: 2026 is 2026/27. Extracted from
 * `leagueGroupLabel`, which inlined it, because an error's `season_year` needs it too. */
export function seasonLabel(year: number): string {
  return `${String(year)}/${String((year + 1) % 100).padStart(2, '0')}`
}
